/**
 * Issue Contract — Canonical validation issue model for the governance pipeline.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.0
 *
 * This replaces the scattered GovernanceIssue, UnifiedIssue, VisualValidationIssue,
 * and ValidationError types with a single, deterministic issue contract.
 *
 * Design principles:
 * - N5: Issue determinism — same input → same issues (signature-based dedup)
 * - N4: Issue Contract governs gates; scoring never overrides blockers
 * - N6: No silent data correction in production
 */

// ─── Issue Kinds ──────────────────────────────────────────────────────────────

/** Issue kind determines gate behavior */
export type IssueKind = 'blocker' | 'coach' | 'info'

// ─── Issue Code Registry ──────────────────────────────────────────────────────
// RESOLVES: CONFLICT-11 (Issue codes defined but never enumerated)
//
// Canonical registry of all issue codes emitted by pipeline stages.
// Provides compile-time safety and documentation for each code.

export const ISSUE_CODES = {
  // ── Schema Stage (Stage 1) ──────────────────────────────────────────────
  SCHEMA_MISSING_ID: 'SCHEMA_MISSING_ID',
  SCHEMA_MISSING_FAMILY_ID: 'SCHEMA_MISSING_FAMILY_ID',
  SCHEMA_MISSING_MODULE: 'SCHEMA_MISSING_MODULE',
  SCHEMA_MISSING_QUESTION_TEXT: 'SCHEMA_MISSING_QUESTION_TEXT',
  SCHEMA_MISSING_QUESTION_TYPE: 'SCHEMA_MISSING_QUESTION_TYPE',
  SCHEMA_UNKNOWN_FAMILY: 'SCHEMA_UNKNOWN_FAMILY',
  SCHEMA_MODULE_MISMATCH: 'SCHEMA_MODULE_MISMATCH',
  SCHEMA_TIER_MISMATCH: 'SCHEMA_TIER_MISMATCH',
  SCHEMA_SKILL_MISMATCH: 'SCHEMA_SKILL_MISMATCH',
  SCHEMA_SUBSKILL_MISMATCH: 'SCHEMA_SUBSKILL_MISMATCH',
  SCHEMA_MC_MISSING_OPTIONS: 'SCHEMA_MC_MISSING_OPTIONS',
  SCHEMA_MC_EMPTY_OPTION: 'SCHEMA_MC_EMPTY_OPTION',
  SCHEMA_MC_DUPLICATE_OPTIONS: 'SCHEMA_MC_DUPLICATE_OPTIONS',
  SCHEMA_MC_MISSING_CORRECT_ANSWER: 'SCHEMA_MC_MISSING_CORRECT_ANSWER',
  SCHEMA_MC_INVALID_CORRECT_ANSWER: 'SCHEMA_MC_INVALID_CORRECT_ANSWER',
  SCHEMA_MISSING_EXPLANATION: 'SCHEMA_MISSING_EXPLANATION',
  SCHEMA_ID_FORMAT: 'SCHEMA_ID_FORMAT',
  SCHEMA_MC_TOO_FEW_OPTIONS: 'SCHEMA_MC_TOO_FEW_OPTIONS',
  SCHEMA_MC_TOO_MANY_OPTIONS: 'SCHEMA_MC_TOO_MANY_OPTIONS',
  SCHEMA_FILL_IN_MISSING_ACCEPTED: 'SCHEMA_FILL_IN_MISSING_ACCEPTED',
  SCHEMA_FILL_IN_EMPTY_ANSWER: 'SCHEMA_FILL_IN_EMPTY_ANSWER',
  SCHEMA_MISSING_CONCEPT_ID: 'SCHEMA_MISSING_CONCEPT_ID',
  SCHEMA_MISSING_VARIANT_INDEX: 'SCHEMA_MISSING_VARIANT_INDEX',

  // ── Type Stage (Stage 1b) ──────────────────────────────────────────────
  TYPE_UNKNOWN: 'TYPE_UNKNOWN',
  TYPE_DEPRECATED_INTERACTIVE_CONFIG: 'TYPE_DEPRECATED_INTERACTIVE_CONFIG',
  TYPE_USING_LEGACY_DATA: 'TYPE_USING_LEGACY_DATA',
  TYPE_MISSING_DATA: 'TYPE_MISSING_DATA',
  TYPE_MISSING_DATA_FIELD: 'TYPE_MISSING_DATA_FIELD',
  // Clock
  TYPE_CLOCK_INVALID_MODE: 'TYPE_CLOCK_INVALID_MODE',
  TYPE_CLOCK_INVALID_HOURS: 'TYPE_CLOCK_INVALID_HOURS',
  TYPE_CLOCK_INVALID_MINUTES: 'TYPE_CLOCK_INVALID_MINUTES',
  TYPE_CLOCK_MC_MISSING_OPTIONS: 'TYPE_CLOCK_MC_MISSING_OPTIONS',
  // Money
  TYPE_MONEY_INVALID_MODE: 'TYPE_MONEY_INVALID_MODE',
  TYPE_MONEY_INVALID_TARGET: 'TYPE_MONEY_INVALID_TARGET',
  TYPE_MONEY_INVALID_DENOM: 'TYPE_MONEY_INVALID_DENOM',
  TYPE_MONEY_INVALID_DENOM_VALUE: 'TYPE_MONEY_INVALID_DENOM_VALUE',
  TYPE_MONEY_INVALID_DENOM_TYPE: 'TYPE_MONEY_INVALID_DENOM_TYPE',
  TYPE_MONEY_DENOMS_NOT_ARRAY: 'TYPE_MONEY_DENOMS_NOT_ARRAY',
  TYPE_MONEY_MISSING_PRICE: 'TYPE_MONEY_MISSING_PRICE',
  TYPE_MONEY_MISSING_PAID: 'TYPE_MONEY_MISSING_PAID',
  TYPE_MONEY_PAID_LESS_THAN_PRICE: 'TYPE_MONEY_PAID_LESS_THAN_PRICE',
  // Ordering
  TYPE_ORDERING_LENGTH_MISMATCH: 'TYPE_ORDERING_LENGTH_MISMATCH',
  TYPE_ORDERING_INVALID_PERMUTATION: 'TYPE_ORDERING_INVALID_PERMUTATION',
  TYPE_ORDERING_EMPTY_ITEM: 'TYPE_ORDERING_EMPTY_ITEM',
  // Matching
  TYPE_MATCHING_TOO_FEW: 'TYPE_MATCHING_TOO_FEW',
  TYPE_MATCHING_INVALID_PAIR: 'TYPE_MATCHING_INVALID_PAIR',
  TYPE_MATCHING_EMPTY_LEFT: 'TYPE_MATCHING_EMPTY_LEFT',
  TYPE_MATCHING_EMPTY_RIGHT: 'TYPE_MATCHING_EMPTY_RIGHT',
  TYPE_MATCHING_NO_DATA: 'TYPE_MATCHING_NO_DATA',
  TYPE_MATCHING_INVALID_LEFT_IDX: 'TYPE_MATCHING_INVALID_LEFT_IDX',
  TYPE_MATCHING_INVALID_RIGHT_IDX: 'TYPE_MATCHING_INVALID_RIGHT_IDX',
  // Table/Schedule
  TYPE_TABLE_ROW_NOT_ARRAY: 'TYPE_TABLE_ROW_NOT_ARRAY',
  TYPE_TABLE_ROW_LENGTH_MISMATCH: 'TYPE_TABLE_ROW_LENGTH_MISMATCH',
  TYPE_SCHEDULE_ROW_NOT_ARRAY: 'TYPE_SCHEDULE_ROW_NOT_ARRAY',
  TYPE_SCHEDULE_ROW_LENGTH_MISMATCH: 'TYPE_SCHEDULE_ROW_LENGTH_MISMATCH',
  // Charts
  TYPE_BAR_CHART_INVALID_ENTRY: 'TYPE_BAR_CHART_INVALID_ENTRY',
  TYPE_BAR_CHART_EMPTY_LABEL: 'TYPE_BAR_CHART_EMPTY_LABEL',
  TYPE_BAR_CHART_VALUE_NOT_NUMBER: 'TYPE_BAR_CHART_VALUE_NOT_NUMBER',
  TYPE_BAR_CHART_TOO_FEW: 'TYPE_BAR_CHART_TOO_FEW',
  TYPE_BAR_CHART_LENGTH_MISMATCH: 'TYPE_BAR_CHART_LENGTH_MISMATCH',
  TYPE_BAR_CHART_NO_DATA: 'TYPE_BAR_CHART_NO_DATA',
  TYPE_LINE_CHART_INVALID_ENTRY: 'TYPE_LINE_CHART_INVALID_ENTRY',
  TYPE_LINE_CHART_VALUE_NOT_NUMBER: 'TYPE_LINE_CHART_VALUE_NOT_NUMBER',
  TYPE_LINE_CHART_LENGTH_MISMATCH: 'TYPE_LINE_CHART_LENGTH_MISMATCH',
  TYPE_LINE_CHART_NO_DATA: 'TYPE_LINE_CHART_NO_DATA',
  TYPE_PIE_CHART_LENGTH_MISMATCH: 'TYPE_PIE_CHART_LENGTH_MISMATCH',
  TYPE_PIE_CHART_VALUE_NOT_NUMBER: 'TYPE_PIE_CHART_VALUE_NOT_NUMBER',
  TYPE_PIE_CHART_EMPTY_LABEL: 'TYPE_PIE_CHART_EMPTY_LABEL',
  TYPE_PIE_CHART_INVALID_CORRECT_INDEX: 'TYPE_PIE_CHART_INVALID_CORRECT_INDEX',
  TYPE_PIE_CHART_MISSING_CORRECT_INDEX: 'TYPE_PIE_CHART_MISSING_CORRECT_INDEX',
  TYPE_PIE_CHART_TOO_FEW_SLICES: 'TYPE_PIE_CHART_TOO_FEW_SLICES',
  // Ratio table / Number sequence
  TYPE_RATIO_TABLE_HEADERS: 'TYPE_RATIO_TABLE_HEADERS',
  TYPE_RATIO_TABLE_ROW_FORMAT: 'TYPE_RATIO_TABLE_ROW_FORMAT',
  TYPE_RATIO_TABLE_MISSING_CELL: 'TYPE_RATIO_TABLE_MISSING_CELL',
  TYPE_RATIO_TABLE_NO_ANSWER: 'TYPE_RATIO_TABLE_NO_ANSWER',
  TYPE_NUMBER_SEQ_TOO_SHORT: 'TYPE_NUMBER_SEQ_TOO_SHORT',
  TYPE_NUMBER_SEQ_INVALID_INDEX: 'TYPE_NUMBER_SEQ_INVALID_INDEX',
  TYPE_NUMBER_SEQ_NO_ANSWER: 'TYPE_NUMBER_SEQ_NO_ANSWER',
  // Text highlight
  TYPE_TEXT_HIGHLIGHT_TOO_FEW_SEGMENTS: 'TYPE_TEXT_HIGHLIGHT_TOO_FEW_SEGMENTS',
  TYPE_TEXT_HIGHLIGHT_NO_CORRECT_SEGMENT:
    'TYPE_TEXT_HIGHLIGHT_NO_CORRECT_SEGMENT',
  TYPE_TEXT_HIGHLIGHT_INVALID_SEGMENT: 'TYPE_TEXT_HIGHLIGHT_INVALID_SEGMENT',
  TYPE_TEXT_HIGHLIGHT_SEGMENT_NO_TEXT: 'TYPE_TEXT_HIGHLIGHT_SEGMENT_NO_TEXT',
  TYPE_TEXT_HIGHLIGHT_NO_DATA: 'TYPE_TEXT_HIGHLIGHT_NO_DATA',
  TYPE_TEXT_HIGHLIGHT_INVALID_RANGE: 'TYPE_TEXT_HIGHLIGHT_INVALID_RANGE',
  TYPE_TEXT_HIGHLIGHT_OUT_OF_RANGE: 'TYPE_TEXT_HIGHLIGHT_OUT_OF_RANGE',
  // Drag drop fill
  TYPE_DRAG_DROP_NO_TEMPLATE: 'TYPE_DRAG_DROP_NO_TEMPLATE',
  TYPE_DRAG_DROP_NO_PLACEHOLDERS: 'TYPE_DRAG_DROP_NO_PLACEHOLDERS',
  TYPE_DRAG_DROP_EMPTY_DRAGGABLE: 'TYPE_DRAG_DROP_EMPTY_DRAGGABLE',
  TYPE_DRAG_DROP_INVALID_PLACEMENT: 'TYPE_DRAG_DROP_INVALID_PLACEMENT',
  // Fill in
  TYPE_FILL_IN_NUMERIC_ANSWER: 'TYPE_FILL_IN_NUMERIC_ANSWER',
  TYPE_FILL_IN_REDUNDANT_CORRECT_ANSWER:
    'TYPE_FILL_IN_REDUNDANT_CORRECT_ANSWER',
  // Column arithmetic
  TYPE_COLUMN_NEGATIVE_OPERAND: 'TYPE_COLUMN_NEGATIVE_OPERAND',
  TYPE_COLUMN_SUB_NEGATIVE_RESULT: 'TYPE_COLUMN_SUB_NEGATIVE_RESULT',
  TYPE_COLUMN_NON_INTEGER: 'TYPE_COLUMN_NON_INTEGER',

  // ── Graph Stage (Stage 2) ──────────────────────────────────────────────
  GRAPH_UNKNOWN_PREREQUISITE: 'GRAPH_UNKNOWN_PREREQUISITE',
  GRAPH_CYCLE_DETECTED: 'GRAPH_CYCLE_DETECTED',
  GRAPH_ORPHAN_NODE: 'GRAPH_ORPHAN_NODE',
  GRAPH_TIER_MISMATCH: 'GRAPH_TIER_MISMATCH',
  GRAPH_FAMILY_NOT_IN_LDG: 'GRAPH_FAMILY_NOT_IN_LDG',
  GRAPH_NO_CONTENT: 'GRAPH_NO_CONTENT',
  GRAPH_DEEP_PREREQUISITE_CHAIN: 'GRAPH_DEEP_PREREQUISITE_CHAIN',

  // ── Content Stage (Stage 3) ────────────────────────────────────────────
  CONTENT_FORBIDDEN_VOCAB: 'CONTENT_FORBIDDEN_VOCAB',
  CONTENT_QUESTION_TOO_LONG: 'CONTENT_QUESTION_TOO_LONG',
  CONTENT_CONTEXT_TOO_LONG: 'CONTENT_CONTEXT_TOO_LONG',
  CONTENT_EXPLANATION_TOO_SHORT: 'CONTENT_EXPLANATION_TOO_SHORT',
  CONTENT_GENERIC_EXPLANATION: 'CONTENT_GENERIC_EXPLANATION',
  CONTENT_EXPLANATION_NOT_DIDACTIC: 'CONTENT_EXPLANATION_NOT_DIDACTIC',
  CONTENT_JARGON_DETECTED: 'CONTENT_JARGON_DETECTED',
  CONTENT_EXPLANATION_PASSIVE: 'CONTENT_EXPLANATION_PASSIVE',
  CONTENT_SENTENCE_TOO_LONG: 'CONTENT_SENTENCE_TOO_LONG',
  CONTENT_MISSING_RULE_REF: 'CONTENT_MISSING_RULE_REF',
  CONTENT_MISSING_CHECK_STRATEGY: 'CONTENT_MISSING_CHECK_STRATEGY',
  CONTENT_MISSING_DISTRACTOR_EXPLANATION:
    'CONTENT_MISSING_DISTRACTOR_EXPLANATION',
  CONTENT_DISTRACTOR_TOO_SHORT: 'CONTENT_DISTRACTOR_TOO_SHORT',
  CONTENT_GENERIC_DISTRACTOR: 'CONTENT_GENERIC_DISTRACTOR',
  CONTENT_NO_MISCONCEPTION_REF: 'CONTENT_NO_MISCONCEPTION_REF',
  CONTENT_MISSING_LATEX: 'CONTENT_MISSING_LATEX',
  CONTENT_DOT_DECIMAL: 'CONTENT_DOT_DECIMAL',
  CONTENT_EURO_DOT: 'CONTENT_EURO_DOT',
  CONTENT_MISSING_SUPERSCRIPT: 'CONTENT_MISSING_SUPERSCRIPT',
  CONTENT_MISSING_SOLUTION_STEPS: 'CONTENT_MISSING_SOLUTION_STEPS',
  CONTENT_MISSING_CITO_DIFFICULTY: 'CONTENT_MISSING_CITO_DIFFICULTY',
  CONTENT_MISSING_CITO_DISCRIMINATION: 'CONTENT_MISSING_CITO_DISCRIMINATION',
  // Forbidden opener check (independent of text length/substance)
  CONTENT_FORBIDDEN_OPENER: 'CONTENT_FORBIDDEN_OPENER',
  // Plus-level content checks
  // Tier-band cognitive level checks (all tiers, not just plus)
  CONTENT_BASIS_COGNITIVE_TOO_HIGH: 'CONTENT_BASIS_COGNITIVE_TOO_HIGH',
  CONTENT_EIND_COGNITIVE_TOO_LOW: 'CONTENT_EIND_COGNITIVE_TOO_LOW',
  CONTENT_PLUS_COGNITIVE_TOO_LOW: 'CONTENT_PLUS_COGNITIVE_TOO_LOW',
  CONTENT_PLUS_DIFFICULTY_TOO_LOW: 'CONTENT_PLUS_DIFFICULTY_TOO_LOW',
  CONTENT_PLUS_MISSING_STEPS: 'CONTENT_PLUS_MISSING_STEPS',
  CONTENT_PLUS_FEW_STEPS: 'CONTENT_PLUS_FEW_STEPS',
  CONTENT_PLUS_SINGLE_STEP: 'CONTENT_PLUS_SINGLE_STEP',
  // Distractor value plausibility checks (§18 D-QUAL)
  CONTENT_WEAK_DISTRACTOR_SCALE: 'CONTENT_WEAK_DISTRACTOR_SCALE',
  CONTENT_WEAK_DISTRACTOR_IMPLAUSIBLE: 'CONTENT_WEAK_DISTRACTOR_IMPLAUSIBLE',
  // General distractor outlier detection (§18 D-QUAL-3)
  CONTENT_WEAK_DISTRACTOR_OUTLIER: 'CONTENT_WEAK_DISTRACTOR_OUTLIER',
  // citoDifficulty / citoDiscrimination range checks
  CONTENT_CITO_DIFFICULTY_OUT_OF_RANGE: 'CONTENT_CITO_DIFFICULTY_OUT_OF_RANGE',
  CONTENT_CITO_DIFFICULTY_BAND_MISMATCH:
    'CONTENT_CITO_DIFFICULTY_BAND_MISMATCH',
  CONTENT_CITO_DISCRIMINATION_OUT_OF_RANGE:
    'CONTENT_CITO_DISCRIMINATION_OUT_OF_RANGE',
  CONTENT_CITO_DISCRIMINATION_BAND_MISMATCH:
    'CONTENT_CITO_DISCRIMINATION_BAND_MISMATCH',
  CONTENT_QUESTION_SENTENCE_TOO_LONG: 'CONTENT_QUESTION_SENTENCE_TOO_LONG',

  // ── Visual Stage (Stage 4) ─────────────────────────────────────────────
  VISUAL_STIMULUS_REQUIRED: 'VISUAL_STIMULUS_REQUIRED',
  VISUAL_SUPPORT_RECOMMENDED: 'VISUAL_SUPPORT_RECOMMENDED',
  VISUAL_STIMULUS_INVALID_TYPE: 'VISUAL_STIMULUS_INVALID_TYPE',
  VISUAL_SUPPORT_INVALID_TYPE: 'VISUAL_SUPPORT_INVALID_TYPE',
  VISUAL_ANSWER_LEAKAGE: 'VISUAL_ANSWER_LEAKAGE',
  VISUAL_STIMULUS_TYPE_MISMATCH: 'VISUAL_STIMULUS_TYPE_MISMATCH',
  VISUAL_SUPPORT_TYPE_MISMATCH: 'VISUAL_SUPPORT_TYPE_MISMATCH',
  VISUAL_UNKNOWN_AID_TYPE: 'VISUAL_UNKNOWN_AID_TYPE',
  VISUAL_DATA_SCHEMA_VIOLATION: 'VISUAL_DATA_SCHEMA_VIOLATION',
  VISUAL_SUPPORT_MISSING_SHOW_WHEN: 'VISUAL_SUPPORT_MISSING_SHOW_WHEN',
  VISUAL_SUPPORT_SHOW_WHEN_MISMATCH: 'VISUAL_SUPPORT_SHOW_WHEN_MISMATCH',
  VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY:
    'VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY',

  // ── Type Stage — New interactive types ─────────────────────────────────
  TYPE_TRUE_FALSE_TOO_FEW: 'TYPE_TRUE_FALSE_TOO_FEW',
  TYPE_TRUE_FALSE_INVALID_STATEMENT: 'TYPE_TRUE_FALSE_INVALID_STATEMENT',
  TYPE_TRUE_FALSE_ANSWERS_MISMATCH: 'TYPE_TRUE_FALSE_ANSWERS_MISMATCH',
  TYPE_TRUE_FALSE_INVALID_ANSWER: 'TYPE_TRUE_FALSE_INVALID_ANSWER',
  TYPE_CATEGORIZE_TOO_FEW_CATEGORIES: 'TYPE_CATEGORIZE_TOO_FEW_CATEGORIES',
  TYPE_CATEGORIZE_TOO_FEW_ITEMS: 'TYPE_CATEGORIZE_TOO_FEW_ITEMS',
  TYPE_CATEGORIZE_INVALID_ITEM: 'TYPE_CATEGORIZE_INVALID_ITEM',
  TYPE_CATEGORIZE_INVALID_MAPPING: 'TYPE_CATEGORIZE_INVALID_MAPPING',
  TYPE_ERROR_SPOTTING_NO_TEXT: 'TYPE_ERROR_SPOTTING_NO_TEXT',
  TYPE_ERROR_SPOTTING_NO_ERRORS: 'TYPE_ERROR_SPOTTING_NO_ERRORS',
  TYPE_ERROR_SPOTTING_INVALID_ERROR: 'TYPE_ERROR_SPOTTING_INVALID_ERROR',
  TYPE_GRID_SHADE_INVALID_DIMS: 'TYPE_GRID_SHADE_INVALID_DIMS',
  TYPE_GRID_SHADE_INVALID_TARGET: 'TYPE_GRID_SHADE_INVALID_TARGET',
  TYPE_GRID_SHADE_INVALID_MODE: 'TYPE_GRID_SHADE_INVALID_MODE',
  TYPE_STEP_SOLVER_NO_STEPS: 'TYPE_STEP_SOLVER_NO_STEPS',
  TYPE_STEP_SOLVER_INVALID_STEP: 'TYPE_STEP_SOLVER_INVALID_STEP',
  TYPE_SENTENCE_PARSER_NO_WORDS: 'TYPE_SENTENCE_PARSER_NO_WORDS',
  TYPE_SENTENCE_PARSER_NO_LABELS: 'TYPE_SENTENCE_PARSER_NO_LABELS',
  TYPE_SENTENCE_PARSER_INVALID_ASSIGNMENT:
    'TYPE_SENTENCE_PARSER_INVALID_ASSIGNMENT',
  TYPE_COMPARISON_TOO_FEW_ITEMS: 'TYPE_COMPARISON_TOO_FEW_ITEMS',
  TYPE_COMPARISON_NO_CRITERION: 'TYPE_COMPARISON_NO_CRITERION',
  TYPE_COMPARISON_INVALID_INDEX: 'TYPE_COMPARISON_INVALID_INDEX',

  // ── Support Stage (Stage 5) ────────────────────────────────────────────
  SUPPORT_FAMILY_NOT_IN_REGISTRY: 'SUPPORT_FAMILY_NOT_IN_REGISTRY',
  SUPPORT_HINTS_EMPTY: 'SUPPORT_HINTS_EMPTY',
  SUPPORT_HINTS_PRESENT: 'SUPPORT_HINTS_PRESENT',
  SUPPORT_EXPLANATION_REQUIRED: 'SUPPORT_EXPLANATION_REQUIRED',
  SUPPORT_VISUAL_REQUIRED: 'SUPPORT_VISUAL_REQUIRED',
  SUPPORT_FAMILY_LOW_QUESTION_COUNT: 'SUPPORT_FAMILY_LOW_QUESTION_COUNT',
  SUPPORT_EXPLANATION_MISSING: 'SUPPORT_EXPLANATION_MISSING',
  SUPPORT_EXPLANATION_TOO_SHORT: 'SUPPORT_EXPLANATION_TOO_SHORT',
  SUPPORT_EXPLANATION_TOO_LONG: 'SUPPORT_EXPLANATION_TOO_LONG',
  SUPPORT_FAMILY_CITO_NO_SPREAD: 'SUPPORT_FAMILY_CITO_NO_SPREAD',
  SUPPORT_FAMILY_CITO_LOW_SPREAD: 'SUPPORT_FAMILY_CITO_LOW_SPREAD',
  SUPPORT_FAMILY_NUMBER_PATTERN_COLLAPSE:
    'SUPPORT_FAMILY_NUMBER_PATTERN_COLLAPSE',
  SUPPORT_FAMILY_LINGUISTIC_TEMPLATE_COLLAPSE:
    'SUPPORT_FAMILY_LINGUISTIC_TEMPLATE_COLLAPSE',

  // ── Seed Stage (Stage 5b) ─────────────────────────────────────────────
  SEED_TIER_AUTOCORRECTED: 'SEED_TIER_AUTOCORRECTED',
  SEED_TIER_INVALID: 'SEED_TIER_INVALID',
  SEED_TIER_OUT_OF_RANGE: 'SEED_TIER_OUT_OF_RANGE',
  SEED_SKILL_AUTOCORRECTED: 'SEED_SKILL_AUTOCORRECTED',
  SEED_SUBSKILL_AUTOCORRECTED: 'SEED_SUBSKILL_AUTOCORRECTED',
  SEED_REJECTED: 'SEED_REJECTED',

  // ── Pedagogy Stage (Stage 3b) — PEDAGOGICAL_CONTRACT_SPEC §8b ──────
  PEDAGOGY_DOK_MISSING: 'PEDAGOGY_DOK_MISSING',
  PEDAGOGY_DOK_TOO_LOW: 'PEDAGOGY_DOK_TOO_LOW',
  PEDAGOGY_DOK_TOO_HIGH: 'PEDAGOGY_DOK_TOO_HIGH',
  PEDAGOGY_INDICATOR_LEAKS_ANSWER: 'PEDAGOGY_INDICATOR_LEAKS_ANSWER',
  PEDAGOGY_DISTRACTORS_UNTAGGED: 'PEDAGOGY_DISTRACTORS_UNTAGGED',
  PEDAGOGY_DISTRACTORS_PARTIAL: 'PEDAGOGY_DISTRACTORS_PARTIAL',
  PEDAGOGY_REGEX_FALLBACK: 'PEDAGOGY_REGEX_FALLBACK',
  PEDAGOGY_MISCONCEPTION_MISSING: 'PEDAGOGY_MISCONCEPTION_MISSING',
  PEDAGOGY_MISCONCEPTION_UNKNOWN: 'PEDAGOGY_MISCONCEPTION_UNKNOWN',
  PEDAGOGY_STRUCTURED_DATA_MISSING: 'PEDAGOGY_STRUCTURED_DATA_MISSING',
} as const

/** Type-safe issue code */
export type IssueCode = (typeof ISSUE_CODES)[keyof typeof ISSUE_CODES]

/** Validation domains map to pipeline stages */
export type IssueDomain =
  | 'schema' // Stage 1: structural validation
  | 'type' // Stage 1b: question type validation
  | 'graph' // Stage 2: LDG/prerequisite validation
  | 'content' // Stage 3: content quality validation
  | 'pedagogy' // Stage 3b: pedagogical contract validation
  | 'visual' // Stage 4: visual aid validation
  | 'support' // Stage 5: support/hint validation
  | 'seed' // Stage 5b: seed consolidation warnings (tier-lock, skill-lock)
  | 'scoring' // Stage 6: score computation
  | 'gate' // Stage 7: gate logic

/** Pipeline stage numbers (1-7) */
export type PipelineStage = 1 | 2 | 3 | 4 | 5 | 6 | 7

// ─── Core Issue Contract ──────────────────────────────────────────────────────

/**
 * ValidationIssue — The canonical issue type for the entire governance pipeline.
 *
 * Every validator in every stage produces ValidationIssue objects.
 * Issues are deduplicated by signature and aggregated by the pipeline orchestrator.
 */
export interface ValidationIssue {
  /** Machine-readable issue code — must be registered in ISSUE_CODES */
  code: IssueCode

  /**
   * Deterministic signature for deduplication.
   * Format: `${domain}::${code}::${field ?? ''}::${normalizedContext}`
   * Same input MUST produce same signature (N5).
   */
  signature: string

  /** Issue kind determines gate behavior */
  kind: IssueKind

  /** Which validation domain produced this issue */
  domain: IssueDomain

  /** Human-readable message (Dutch or English depending on context) */
  message: string

  /** Which field triggered the issue (e.g., "feedback.explanationText", "options[2]") */
  field?: string

  /** What was expected */
  expected?: string

  /** What was actually found */
  actual?: string

  /** Pipeline stage that produced this issue (1-7) */
  stage: PipelineStage

  /**
   * Severity score (0-100).
   * Higher = more severe.
   * Blockers: 70-100, Coach: 30-69, Info: 0-29
   */
  severity: number

  /** Optional fix suggestion */
  suggestion?: string

  /** Optional: which question triggered this */
  questionId?: string

  /** Optional: which family triggered this */
  familyId?: string
}

// ─── Gate Logic ───────────────────────────────────────────────────────────────

/** Gate verdict for a single question */
export type GateVerdict = 'PASS' | 'FAIL' | 'REVIEW_NEEDED'

/** Gate thresholds (configurable via policy) */
export interface GateThresholds {
  /** No blockers + score ≥ this → production eligible */
  productionMinScore: number

  /** No blockers + no high-severity coach + score ≥ this → assessment eligible */
  assessmentMinScore: number

  /** Coach severity ≥ this → REVIEW_NEEDED */
  reviewCoachSeverityThreshold: number

  /** Minimum questions per family for assessment pool */
  assessmentMinQuestionsPerFamily: number
}

/** Default gate thresholds.
 * NOTE: These are fallback values only. The active thresholds come from
 * defaultPolicy.ts GATES via policyEngine.getGateThresholds().
 * Kept aligned with defaultPolicy.ts for consistency (SCAN-042 fix).
 */
export const DEFAULT_GATE_THRESHOLDS: GateThresholds = {
  productionMinScore: 70,
  assessmentMinScore: 82,
  reviewCoachSeverityThreshold: 45,
  assessmentMinQuestionsPerFamily: 3,
}

/** Result of gate evaluation for a single question */
export interface GateResult {
  /** Overall verdict */
  verdict: GateVerdict

  /** Is this question eligible for production (practice mode)? */
  productionEligible: boolean

  /** Is this question eligible for assessment (CITO-style tests)? */
  assessmentEligible: boolean

  /** Does this question need human review? */
  reviewNeeded: boolean

  /** Computed quality score (0-100) */
  score: number

  /** All issues found */
  issues: ValidationIssue[]

  /** Issue counts by kind */
  counts: {
    blockers: number
    coaches: number
    infos: number
    total: number
  }

  /** Reason for verdict (human-readable) */
  reason: string
}

// ─── Pipeline Result ──────────────────────────────────────────────────────────

/** Result of the full pipeline for a single question */
export interface PipelineResult {
  questionId: string
  familyId: string

  /** Gate result (final verdict) */
  gate: GateResult

  /** Per-stage issue breakdown */
  stageResults: Record<PipelineStage, ValidationIssue[]>

  /** Per-domain scores (0-100) */
  domainScores: Record<IssueDomain, number>

  /** Detected cognitive level */
  cognitiveLevel?: string

  /** Stage runtime errors — non-null if a stage threw an unexpected exception */
  stageErrors?: Partial<Record<PipelineStage, string>>

  /** Timestamp of validation run */
  timestamp: number
}

/** Result of a batch pipeline run */
export interface PipelineBatchResult {
  generatedAt: string
  totalQuestions: number

  /** Aggregate counts */
  verdicts: Record<GateVerdict, number>
  productionEligible: number
  assessmentEligible: number
  reviewNeeded: number

  /** Average score */
  avgScore: number

  /** Issue distribution */
  issuesByDomain: Record<IssueDomain, number>
  issuesByKind: Record<IssueKind, number>

  /** Per-question results */
  results: PipelineResult[]
}

// ─── Issue Construction Helpers ───────────────────────────────────────────────

/**
 * Create a deterministic signature for an issue.
 * Ensures N5: same input → same issues.
 */
export function createIssueSignature(
  domain: IssueDomain,
  code: IssueCode,
  field?: string,
  context?: string,
): string {
  const normalizedContext = (context ?? '').trim().toLowerCase().slice(0, 100)
  return `${domain}::${code}::${field ?? ''}::${normalizedContext}`
}

/**
 * Create a blocker issue (blocks production + assessment).
 */
export function blocker(
  domain: IssueDomain,
  stage: PipelineStage,
  code: IssueCode,
  message: string,
  opts?: {
    field?: string
    expected?: string
    actual?: string
    suggestion?: string
    questionId?: string
    familyId?: string
    severity?: number
  },
): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'blocker',
    domain,
    message,
    stage,
    severity: opts?.severity ?? 80,
    field: opts?.field,
    expected: opts?.expected,
    actual: opts?.actual,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
  }
}

/**
 * Create a coach issue (quality improvement suggestion, may trigger review).
 */
export function coach(
  domain: IssueDomain,
  stage: PipelineStage,
  code: IssueCode,
  message: string,
  opts?: {
    field?: string
    expected?: string
    actual?: string
    suggestion?: string
    questionId?: string
    familyId?: string
    severity?: number
  },
): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'coach',
    domain,
    message,
    stage,
    severity: opts?.severity ?? 45,
    field: opts?.field,
    expected: opts?.expected,
    actual: opts?.actual,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
  }
}

/**
 * Create an info issue (informational, no gate impact).
 */
export function info(
  domain: IssueDomain,
  stage: PipelineStage,
  code: IssueCode,
  message: string,
  opts?: {
    field?: string
    suggestion?: string
    questionId?: string
    familyId?: string
    severity?: number
  },
): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'info',
    domain,
    message,
    stage,
    severity: opts?.severity ?? 10,
    field: opts?.field,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
  }
}

// ─── Gate Evaluation ──────────────────────────────────────────────────────────

/**
 * Evaluate gate logic for a set of issues.
 *
 * Gate rules (from architecture doc):
 * - productionGate: no blockers + score ≥ 60
 * - assessmentGate: no blockers + no high-severity coach + score ≥ 75 + type eligible
 * - reviewGate: coach severity ≥ 50 → REVIEW_NEEDED
 */
/**
 * Compute total penalty for a set of issues.
 * Shared by evaluateGate and computeDomainScores in orchestrator.
 */
export function computeIssuePenalty(issues: ValidationIssue[]): number {
  return issues.reduce((sum, issue) => {
    switch (issue.kind) {
      case 'blocker':
        return sum + 30
      case 'coach':
        return sum + Math.round(issue.severity / 4)
      case 'info':
        return sum + 2
      default:
        return sum
    }
  }, 0)
}

export function evaluateGate(
  issues: ValidationIssue[],
  thresholds: GateThresholds = DEFAULT_GATE_THRESHOLDS,
): GateResult {
  const blockers = issues.filter((i) => i.kind === 'blocker')
  const coaches = issues.filter((i) => i.kind === 'coach')
  const infos = issues.filter((i) => i.kind === 'info')

  // Score: start at 100, subtract penalties
  const penalty = computeIssuePenalty(issues)
  const score = Math.max(0, 100 - penalty)

  // Gate evaluation
  const hasBlockers = blockers.length > 0
  const hasHighSeverityCoach = coaches.some(
    (c) => c.severity >= thresholds.reviewCoachSeverityThreshold,
  )

  const productionEligible =
    !hasBlockers && score >= thresholds.productionMinScore
  const assessmentEligible =
    !hasBlockers &&
    !hasHighSeverityCoach &&
    score >= thresholds.assessmentMinScore
  const reviewNeeded = hasHighSeverityCoach

  // Determine verdict
  let verdict: GateVerdict
  let reason: string

  if (hasBlockers) {
    verdict = 'FAIL'
    reason = `${blockers.length} blocker(s) found: ${blockers.map((b) => b.code).join(', ')}`
  } else if (reviewNeeded) {
    verdict = 'REVIEW_NEEDED'
    reason = `High-severity coach issue(s) require review (score: ${score})`
  } else if (score < thresholds.productionMinScore) {
    verdict = 'FAIL'
    reason = `Score ${score} below production threshold (${thresholds.productionMinScore})`
  } else {
    verdict = 'PASS'
    reason = `Score ${score}, no blockers, ${productionEligible ? 'production' : ''}${assessmentEligible ? '+assessment' : ''} eligible`
  }

  return {
    verdict,
    productionEligible,
    assessmentEligible,
    reviewNeeded,
    score,
    issues,
    counts: {
      blockers: blockers.length,
      coaches: coaches.length,
      infos: infos.length,
      total: issues.length,
    },
    reason,
  }
}

// ─── Deduplication ────────────────────────────────────────────────────────────

/**
 * Deduplicate issues by signature.
 * When duplicates exist, keep the highest-severity one.
 */
export function deduplicateIssues(
  issues: ValidationIssue[],
): ValidationIssue[] {
  const seen = new Map<string, ValidationIssue>()

  for (const issue of issues) {
    const existing = seen.get(issue.signature)
    if (!existing || issue.severity > existing.severity) {
      seen.set(issue.signature, issue)
    }
  }

  return Array.from(seen.values())
}
